import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import SideNav from "components/layout-components/SideNav";
import TopNav from "components/layout-components/TopNav";
import Loading from "components/shared-components/Loading";
import MobileNav from "components/layout-components/MobileNav";
import HeaderNav from "components/layout-components/HeaderNav";
import ModuleHeader from "components/layout-components/ModuleHeader";
import Footer from "components/layout-components/Footer";
import GlobalNavSidebar, {
  GLOBAL_NAV_WIDTH,
  GLOBAL_NAV_COLLAPSED_WIDTH,
} from "components/layout-components/GlobalNavSidebar";
import AppViews from "views/app-views";
import { Layout, Grid } from "antd";
import { Redirect } from "react-router-dom";

import navigationConfig from "configs/NavigationConfig";
import {
  SIDE_NAV_WIDTH,
  SIDE_NAV_COLLAPSED_WIDTH,
  NAV_TYPE_SIDE,
  NAV_TYPE_TOP,
} from "constants/ThemeConstant";
import utils from "utils";
import { APP_PREFIX_PATH } from "configs/AppConfig";
import { getResolvedUserType } from "utils/MyMUtils";
import { findActiveNavEntry, getNavEntryHref, isManagerUser, isTopLevelNavVisible } from "utils/navUtils";
import { useThemeSwitcher } from "react-css-theme-switcher";
import { prewarmModuleSessions } from "utils/prewarmModuleSessions";

const { Content } = Layout;
const { useBreakpoint } = Grid;

export const AppLayout = ({ navCollapsed, navType, location, isSuperAdmin }) => {
  const currentRouteInfo = utils.getRouteInfo(
    navigationConfig,
    location.pathname
  );
  const screens = utils.getBreakPoint(useBreakpoint());
  const isMobile = !screens.includes("lg");
  const isNavTop = navType === NAV_TYPE_TOP;

  const [globalNavCollapsed, setGlobalNavCollapsed] = useState(false);

  // Users with nothing to navigate to (system owner, managers confined to one
  // module via the main menu) get no sidebar at all — the old top nav was
  // simply empty for them.
  const userType = getResolvedUserType();
  const hasNavItems = navigationConfig.some((menu) => isTopLevelNavVisible(menu, isSuperAdmin, userType));

  // Log in to this user's modules in the background, so opening one only
  // waits for its data (see utils/prewarmModuleSessions).
  const visibleNavKeys = navigationConfig
    .filter((menu) => isTopLevelNavVisible(menu, isSuperAdmin, userType))
    .map((menu) => menu.key)
    .join(",");
  useEffect(() => prewarmModuleSessions(visibleNavKeys.split(",")), [visibleNavKeys]);
  const isNavSide = navType === NAV_TYPE_SIDE && hasNavItems;

  // Managers may only use the modules in their sidebar (plus
  // their own My Profile); any other app page sends them to the first one.
  // (external links such as Cleaning Excellence are not pages here, so not a landing place)
  const managerMenus = isManagerUser(isSuperAdmin, userType)
    ? navigationConfig.filter((menu) => isTopLevelNavVisible(menu, isSuperAdmin, userType) && !menu.simpleExternal)
    : null;
  const managerBlocked =
    managerMenus &&
    managerMenus.length > 0 &&
    ![`${APP_PREFIX_PATH}/my-profile`, `${APP_PREFIX_PATH}/change-password`].includes(location.pathname) &&
    !findActiveNavEntry(managerMenus, location.pathname);
  const showGlobalNav = false; // Removed left menu

  const globalNavCurrentWidth = showGlobalNav
    ? globalNavCollapsed
      ? GLOBAL_NAV_COLLAPSED_WIDTH
      : GLOBAL_NAV_WIDTH
    : 0;

  const getLayoutGutter = () => {
    if (isMobile) return 0;
    if (!isNavSide) return globalNavCurrentWidth;
    const sideNavWidth = navCollapsed ? SIDE_NAV_COLLAPSED_WIDTH : SIDE_NAV_WIDTH;
    return globalNavCurrentWidth + sideNavWidth;
  };

  const { status } = useThemeSwitcher();

  if (status === "loading") {
    return <Loading cover="page" />;
  }

  if (managerBlocked) {
    return <Redirect to={getNavEntryHref(managerMenus.find((menu) => menu.key === "manager-dashboard") || managerMenus[0])} />;
  }

  return (
    <Layout>
      <HeaderNav isMobile={isMobile} showMenuToggle={isMobile && isNavSide} />
      {isNavTop ? <TopNav routeInfo={currentRouteInfo} /> : null}
      <Layout className="app-container">
        {isNavSide && !isMobile ? (
          <SideNav style={{ left: globalNavCurrentWidth }} />
        ) : null}
        <Layout
          className="app-layout"
          style={{
            paddingLeft: getLayoutGutter(),
            transition: "padding-left 0.2s ease",
          }}
        >
          <div className={`app-content ${isNavTop ? "layout-top-nav" : ""}`}>
            <ModuleHeader />
            <Content>
              <AppViews />
            </Content>
          </div>
          <Footer />
        </Layout>
      </Layout>
      {showGlobalNav && (
        <GlobalNavSidebar
          collapsed={globalNavCollapsed}
          onToggle={() => setGlobalNavCollapsed((c) => !c)}
          topOffset={isNavTop ? 125 : 70}
        />
      )}
      {isMobile && isNavSide && <MobileNav />}
    </Layout>
  );
};

const mapStateToProps = ({ theme, userAuth }) => {
  const { navCollapsed, navType, locale } = theme;
  return { navCollapsed, navType, locale, isSuperAdmin: Number(userAuth?.isSuperAdmin) === 1 };
};

export default connect(mapStateToProps)(React.memo(AppLayout));
